import { addDoc, collection, serverTimestamp, Timestamp } from 'firebase/firestore';
import firebaseFirestore from '~/config/firebase/firestore';
import { detectarOrigen, llegoConCodigo } from '~/utils/embedOrigin';

/**
 * Rastro de todo lo que le pasa a una reserva: quién la hizo, quién la cambió,
 * quién la canceló y desde dónde.
 *
 * Vive aparte de `activity_logs` a propósito. Aquella colección mezcla veinte
 * cosas distintas y guarda volcados enormes: medido en producción, un solo
 * registro de modificación pesa 64 KB de media y el mayor llegaba a 950 KB,
 * porque copiaba el mapa de ocupación entero antes y después. Además, el 49%
 * de sus registros no identifica a nadie y el 42% no dice de qué empresa son.
 *
 * Aquí se guarda lo contrario: un evento por acción, siempre con empresa,
 * siempre con autor, y **solo los campos que cambiaron**, nunca el mapa de
 * ocupación. Así un evento pesa menos de 1 KB y se puede responder de un
 * vistazo "qué pasó con esta reserva".
 *
 * Regla de oro: registrar nunca puede impedir reservar. Todo va en try/catch
 * y ningún fallo aquí interrumpe el flujo del cliente.
 */

export const COLECCION_EVENTOS = 'reservation_events';

export type TipoEvento = 'created' | 'modified' | 'cancelled' | 'status';

/** Un campo que cambió, ya convertido a texto legible. */
export type CambioEvento = {
  field: string;
  before: string;
  after: string;
};

export type ActorEvento = {
  /**
   * `staff` = alguien con sesión en el panel. `customer` = el cliente final.
   * `unknown` = se sabe que pasó, pero no quién: de algunos hechos antiguos
   * solo queda el rastro indirecto, y suponer un autor sería inventarlo.
   */
  kind: 'staff' | 'customer' | 'unknown';
  uid: string | null;
  email: string | null;
  name: string | null;
  role: string | null;
};

export type OrigenEvento = {
  /** Dónde estaba la persona: el panel interno o el formulario público. */
  channel: 'dashboard' | 'form';
  /** El parámetro `from` de la URL: `sistema`, `usuarios-noi`, etc. */
  from: string | null;
  /** Dominio de la web que embebe el formulario, si se pudo averiguar. */
  parentDomain: string | null;
  /** Llegó con un código de reserva, es decir, desde el correo. */
  withCode: boolean;
  /** Resumen corto del navegador. El User-Agent completo no aporta nada aquí. */
  device: string;
};

export type EventoReserva = {
  id?: string;
  type: TipoEvento;
  reservationId: string;
  code: string;
  companyId: string;
  locationId: string;
  locationName: string;
  /** Hora del servidor: es la que vale para auditar. */
  at: Timestamp | null;
  /** Hora del navegador. Sirve para ordenar al instante y para detectar relojes desajustados. */
  clientAt: Timestamp | Date;
  actor: ActorEvento;
  origin: OrigenEvento;
  changes: CambioEvento[];
  customer: { name: string; email: string };
  /** Frase corta ya redactada, para poder leer el historial sin interpretar nada. */
  summary: string;
  /** Reconstruido a partir del histórico, no observado en el momento. */
  derived?: boolean;
  /**
   * La fecha que lleva sirve solo para ordenar; el texto explica por qué no se
   * puede afirmar. Una nota es preferible a una hora inventada.
   */
  notaFecha?: { corta: string; detalle: string };
  /** Qué decir del origen cuando no se puede saber desde dónde se hizo. */
  origenNota?: string;
};

/* ────────────────────────────  quién  ──────────────────────────── */

type UsuarioSesion = {
  uid?: string;
  email?: string;
  name?: { firstName?: string; lastName?: string };
  role?: string;
};

/**
 * Quién está actuando. Sin sesión es el cliente final.
 *
 * Ojo con una trampa conocida: el personal de NOI a veces usa el formulario
 * público sin haber iniciado sesión, y entonces sus acciones se registran como
 * del cliente. No se puede distinguir desde el navegador; lo que sí queda es el
 * origen (`origin.channel` y `origin.from`), que da la pista.
 */
export const describirActor = (usuario?: UsuarioSesion | null): ActorEvento => {
  const uid = usuario?.uid ?? null;
  if (!uid) {
    return { kind: 'customer', uid: null, email: null, name: null, role: null };
  }
  // Sin duplicados: hay cuentas cuyo nombre y apellido son el mismo texto
  // (a veces el propio correo), y unirlos a secas lo repetía dos veces.
  const partes = [usuario?.name?.firstName, usuario?.name?.lastName]
    .map((parte) => (parte ?? '').trim())
    .filter(Boolean);
  const nombre = [...new Set(partes)].join(' ').trim();
  return {
    kind: 'staff',
    uid,
    email: usuario?.email ?? null,
    name: nombre || null,
    role: usuario?.role ?? null,
  };
};

/* ───────────────────────────  desde dónde  ─────────────────────────── */

/** `Mozilla/5.0 (Macintosh…) Chrome/152…` → `Chrome en Mac`. */
export const resumirDispositivo = (userAgent: string): string => {
  const ua = userAgent || '';
  const navegador =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Navegador';
  const sistema =
    /iPhone/.test(ua) ? 'iPhone'
    : /iPad/.test(ua) ? 'iPad'
    : /Android/.test(ua) ? 'Android'
    : /Macintosh|Mac OS X/.test(ua) ? 'Mac'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux'
    : 'sistema desconocido';
  return `${navegador} en ${sistema}`;
};

type OrigenParams = {
  /** El parámetro `from` de la URL. */
  from?: string | null;
  /**
   * Solo para forzarlo. Si no se indica, se usa lo que se detectó al abrir la
   * página, que es lo fiable: nunca conviene afirmarlo a mano, porque la misma
   * pantalla se alcanza desde el correo y desde la web del cliente.
   */
  conCodigo?: boolean;
  /** Se sabe con certeza que la acción salió del panel interno. */
  esPanel?: boolean;
};

export const describirOrigen = ({
  from,
  conCodigo,
  esPanel,
}: OrigenParams = {}): OrigenEvento => {
  let parentDomain: string | null = null;
  let device = 'desconocido';
  try {
    parentDomain = detectarOrigen().dominioPadre;
    device = resumirDispositivo(navigator.userAgent);
  } catch {
    /* en un entorno sin navegador se queda con los valores por defecto */
  }
  // `from=sistema` es lo que el panel pone al abrir el formulario embebido en
  // su modal, así que sirve de respaldo cuando no se indica explícitamente.
  const canal: OrigenEvento['channel'] = esPanel || from === 'sistema' ? 'dashboard' : 'form';
  return {
    channel: canal,
    from: from ?? null,
    parentDomain,
    withCode: conCodigo ?? llegoConCodigo,
    device,
  };
};

/* ──────────────────────────  qué cambió  ────────────────────────── */

/** Nombre legible de cada campo, para el historial y los filtros. */
export const NOMBRE_CAMPO: Record<string, string> = {
  location: 'Sede',
  startDate: 'Fecha',
  startHour: 'Hora de inicio',
  endDate: 'Fecha de fin',
  endHour: 'Hora de fin',
  numberPeople: 'Número de personas',
  name: 'Nombre',
  email: 'Correo',
  phone: 'Teléfono',
  status: 'Estado',
};

export const nombreDeCampo = (campo: string): string => NOMBRE_CAMPO[campo] ?? campo;

/** `pending` → `pendiente`. Si no se conoce, se deja tal cual. */
export const etiquetaEstado = (estado?: string | null): string =>
  (estado && ETIQUETA_ESTADO[estado]) || estado || 'sin estado';

/**
 * Valor de un cambio, listo para mostrar.
 *
 * La traducción se hace aquí, al pintar, y no al guardar: los eventos ya
 * registrados no se pueden modificar —es lo que hace fiable el historial— así
 * que los que se guardaron con el estado en inglés se leen igual de bien.
 */
export const valorLegible = (campo: string, valor: string): string =>
  campo === 'status' ? etiquetaEstado(valor) : valor;

const texto = (valor: unknown): string => {
  if (valor === null || valor === undefined || valor === '') return '—';
  if (typeof valor === 'boolean') return valor ? 'sí' : 'no';
  return String(valor);
};

/**
 * Compara dos versiones de una reserva y devuelve solo lo que cambió.
 *
 * Deliberadamente compara **valores sueltos**, nunca estructuras grandes: es
 * justo lo que hinchaba los registros antiguos hasta cientos de kilobytes.
 */
export const compararReserva = (
  antes: Record<string, unknown>,
  despues: Record<string, unknown>,
  campos: string[] = Object.keys(NOMBRE_CAMPO)
): CambioEvento[] => {
  const cambios: CambioEvento[] = [];
  for (const campo of campos) {
    const a = texto(antes?.[campo]);
    const b = texto(despues?.[campo]);
    if (a !== b) cambios.push({ field: campo, before: a, after: b });
  }
  return cambios;
};

/* ────────────────────────────  redacción  ──────────────────────────── */

const quienEs = (actor: ActorEvento): string => {
  if (actor.kind === 'unknown') return 'Alguien';
  if (actor.kind === 'customer') return 'El cliente';
  return actor.name || actor.email || 'Alguien del equipo';
};

export const redactarResumen = (
  tipo: TipoEvento,
  actor: ActorEvento,
  cambios: CambioEvento[],
  locationName: string
): string => {
  const quien = quienEs(actor);
  switch (tipo) {
    case 'created':
      return `${quien} creó la reserva en ${locationName}`;
    case 'cancelled':
      return `${quien} canceló la reserva de ${locationName}`;
    case 'status': {
      const estado = cambios.find((c) => c.field === 'status');
      return `${quien} cambió el estado a ${estado?.after ?? 'otro'}`;
    }
    case 'modified': {
      if (cambios.length === 0) return `${quien} guardó la reserva sin cambios`;
      const nombres = cambios.map((c) => nombreDeCampo(c.field).toLowerCase());
      const lista =
        nombres.length === 1
          ? nombres[0]
          : `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
      return `${quien} cambió ${lista}`;
    }
    default:
      return `${quien} actuó sobre la reserva`;
  }
};

/**
 * Saca el id de la empresa venga como venga.
 *
 * En `reservations` el campo `location.company` se guarda como referencia de
 * Firestore, no como texto: al leerlo llega un objeto con `.id`. Pasarlo tal
 * cual dejaría el evento sin empresa, que es exactamente el fallo que tienen
 * hoy el 42% de los registros de `activity_logs`.
 */
export const idDeEmpresa = (valor: unknown): string => {
  if (!valor) return '';
  if (typeof valor === 'string') return valor;
  const ref = valor as { id?: string; path?: string };
  if (typeof ref.id === 'string') return ref.id;
  if (typeof ref.path === 'string') return ref.path.split('/').pop() ?? '';
  return '';
};

/* ────────────────────────────  escritura  ──────────────────────────── */

export type RegistrarParams = {
  tipo: TipoEvento;
  reservationId: string;
  code?: string | null;
  companyId?: string | null;
  locationId?: string | null;
  locationName?: string | null;
  usuario?: UsuarioSesion | null;
  origen?: OrigenParams;
  cambios?: CambioEvento[];
  /** Los campos del formulario son dinámicos: pueden llegar como número. */
  cliente?: { nombre?: unknown; correo?: unknown };
  /** Para sobrescribir la frase redactada automáticamente. */
  resumen?: string;
};

/**
 * Deja constancia de una acción sobre una reserva.
 *
 * No lanza nunca y no hay que esperarla para continuar: si el registro falla,
 * la reserva sigue su curso. Devuelve el id del evento, o `null` si no se pudo
 * guardar.
 */
export const registrarEvento = async (params: RegistrarParams): Promise<string | null> => {
  try {
    const actor = describirActor(params.usuario);
    const origin = describirOrigen(params.origen);
    const cambios = params.cambios ?? [];
    const locationName = params.locationName ?? 'la sede';

    const evento: Omit<EventoReserva, 'id'> = {
      type: params.tipo,
      reservationId: params.reservationId,
      code: (params.code ?? '').toUpperCase(),
      companyId: params.companyId ?? '',
      locationId: params.locationId ?? '',
      locationName,
      at: serverTimestamp() as unknown as Timestamp,
      clientAt: new Date(),
      actor,
      origin,
      changes: cambios,
      customer: {
        name: params.cliente?.nombre == null ? '' : String(params.cliente.nombre),
        email: params.cliente?.correo == null ? '' : String(params.cliente.correo),
      },
      summary: params.resumen ?? redactarResumen(params.tipo, actor, cambios, locationName),
    };

    const ref = await addDoc(collection(firebaseFirestore, COLECCION_EVENTOS), evento);
    return ref.id;
  } catch (error) {
    console.error('No se pudo registrar el evento de la reserva:', error);
    return null;
  }
};

/* ─────────────────────  historial de lo que ya existe  ───────────────────── */

const aFecha = (valor: unknown): Date | null => {
  if (!valor) return null;
  const v = valor as { toDate?: () => Date };
  if (typeof v.toDate === 'function') return v.toDate();
  const d = new Date(valor as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Los estados se guardan en inglés; a la vista van en español. */
export const ETIQUETA_ESTADO: Record<string, string> = {
  pending: 'pendiente',
  confirmed: 'confirmada',
  checkin: 'check-in',
  checkout: 'check-out',
  cancelled: 'cancelada',
};

/**
 * Reconstruye el historial de una reserva anterior al registro de eventos.
 *
 * Las reservas ya guardaban pistas sueltas —`createdAt`, `statusHistory`,
 * `cancelledBy`, `from`— pero nadie las juntaba. Esto las convierte en una
 * línea de tiempo legible **sin escribir nada en la base de datos**: no hay
 * migración que pueda salir mal, y lo reconstruido se marca como tal para no
 * hacerlo pasar por observado.
 *
 * Lo que no se puede saber, no se inventa: si la reserva es anterior al sello
 * de autor, el actor queda como desconocido y solo se dice desde dónde vino.
 */
export const reconstruirDesdeReserva = (reserva: Record<string, any>): EventoReserva[] => {
  if (!reserva) return [];

  const locationName = reserva.location?.name ?? 'la sede';
  const base = {
    reservationId: reserva.id ?? '',
    code: (reserva.code ?? '').toUpperCase(),
    companyId: idDeEmpresa(reserva.location?.company),
    locationId: reserva.location?.id ?? '',
    locationName,
    customer: {
      name: reserva.namesAndSurnames ?? reserva.name ?? '',
      email: reserva.email ?? '',
    },
    derived: true as const,
  };

  const desconocido: ActorEvento = {
    kind: 'unknown',
    uid: null,
    email: null,
    name: null,
    role: null,
  };

  // Sin sello de autor solo se sabe el canal: `from: 'sistema'` es el panel.
  const actorPorDefecto = (sello?: ActorEvento): ActorEvento =>
    sello ??
    (reserva.from === 'sistema'
      ? { kind: 'staff', uid: null, email: null, name: null, role: null }
      : { kind: 'customer', uid: null, email: null, name: null, role: null });

  const origen = (): OrigenEvento => ({
    channel: reserva.from === 'sistema' ? 'dashboard' : 'form',
    from: reserva.from ?? null,
    parentDomain: null,
    withCode: false,
    device: 'no registrado',
  });

  const eventos: EventoReserva[] = [];
  const sinHora = (fecha: Date | null) => (fecha ?? new Date(0)) as unknown as Timestamp;

  // ── Creación ───────────────────────────────────────────────────────────
  // La fecha se toma tal cual; que esté pisada solo se puede saber viendo
  // también los eventos registrados, y eso se decide en `useHistorialReserva`.
  const actorCreacion = actorPorDefecto(reserva.createdBy);
  const creada = aFecha(reserva.createdAt);
  eventos.push({
    ...base,
    type: 'created',
    at: sinHora(creada),
    clientAt: creada ?? new Date(0),
    actor: actorCreacion,
    origin: origen(),
    changes: [],
    summary: redactarResumen('created', actorCreacion, [], locationName),
  });

  // ── Cambios de estado ──────────────────────────────────────────────────
  //
  // El estado anterior a cada paso se deduce de la propia secuencia, y eso
  // saca a la luz dos cosas que antes quedaban invisibles:
  //
  //  · Dos "confirmada" seguidas solo pueden significar que entre medias
  //    volvió a "pendiente". Y **el único sitio de todo el proyecto que pone
  //    el estado en 'pending' es guardar una modificación**, así que hubo una
  //    modificación y se muestra.
  //  · Una "confirmada" después de un check-in o un check-out es el botón
  //    "Deshacer check-in", que aparece para ambos estados.
  //
  // Medido en producción: estas tres vías explican las 2.356 confirmaciones
  // repetidas que existen, sin una sola excepción.
  const pasos = ((reserva.statusHistory ?? []) as Array<Record<string, any>>).filter(
    (paso) => paso?.status
  );
  const actualizada = aFecha(reserva.updatedAt)?.getTime() ?? null;

  pasos.forEach((paso, i) => {
    const anterior: string = i === 0 ? 'pending' : pasos[i - 1].status;
    const volvioAPendiente = i > 0 && anterior === 'confirmed' && paso.status === 'confirmed';
    const deshizoCheckin =
      paso.status === 'confirmed' && (anterior === 'checkin' || anterior === 'checkout');

    const tPaso = aFecha(paso.createdAt);
    const tAnterior = i > 0 ? aFecha(pasos[i - 1].createdAt)?.getTime() ?? null : null;

    if (volvioAPendiente) {
      // `updatedAt` guarda la hora de la ÚLTIMA modificación. Si cae dentro de
      // esta ventana es esta; si no, solo se sabe que ocurrió entre los dos
      // pasos, y se coloca en medio sin afirmar la hora.
      const exacta =
        actualizada != null &&
        tAnterior != null &&
        tPaso != null &&
        actualizada > tAnterior &&
        actualizada <= tPaso.getTime();
      const cuando = exacta
        ? new Date(actualizada as number)
        : new Date(Math.round(((tAnterior ?? 0) + (tPaso?.getTime() ?? 0)) / 2));
      eventos.push({
        ...base,
        type: 'modified',
        at: sinHora(cuando),
        clientAt: cuando,
        ...(exacta
          ? {}
          : {
              notaFecha: {
                corta: 'No se registró cuándo',
                detalle:
                  'Se sabe que ocurrió entre los dos cambios de estado que la rodean, ' +
                  'pero de las modificaciones antiguas solo quedó la hora de la última.',
              },
            }),
        actor: desconocido,
        origin: origen(),
        origenNota: 'No se registró desde dónde',
        changes: [],
        summary: 'Se modificó la reserva, y por eso volvió a quedar pendiente',
      });
    }

    const actor: ActorEvento = paso.user
      ? { kind: 'staff', uid: null, email: paso.user, name: null, role: null }
      : { kind: 'staff', uid: null, email: null, name: null, role: null };
    const cambios: CambioEvento[] = [
      {
        field: 'status',
        before: etiquetaEstado(volvioAPendiente ? 'pending' : anterior),
        after: etiquetaEstado(paso.status),
      },
    ];
    eventos.push({
      ...base,
      type: 'status',
      at: sinHora(tPaso),
      clientAt: tPaso ?? new Date(0),
      actor,
      // Confirmar, hacer check-in y check-out solo existen en el panel: las
      // cuatro únicas escrituras de `statusHistory` del proyecto están ahí.
      origin: { ...origen(), channel: 'dashboard', from: null },
      changes: cambios,
      summary: deshizoCheckin
        ? `${actor.email ?? 'Alguien del equipo'} deshizo el check-in`
        : redactarResumen('status', actor, cambios, locationName),
    });
  });

  // ── Cancelación ────────────────────────────────────────────────────────
  if (reserva.status === 'cancelled') {
    const actor: ActorEvento =
      reserva.cancelledByUser ??
      (reserva.cancelledBy === 'customer'
        ? { kind: 'customer', uid: null, email: null, name: null, role: null }
        : { kind: 'staff', uid: null, email: null, name: null, role: null });

    // **No se sabe cuándo se canceló.** Cancelar nunca tocó `updatedAt`:
    // medido en producción, 2.903 de las 2.940 canceladas lo tienen idéntico a
    // `createdAt`. Usarlo colocaba la cancelación al principio y salía
    // "creada → cancelada → confirmada", que es imposible. Se coloca al final
    // —cancelar sí es terminal— y se dice que la hora no se guardó.
    const cancelledAt = aFecha(reserva.cancelledAt);
    const ultimoConocido = Math.max(
      aFecha(reserva.createdAt)?.getTime() ?? 0,
      aFecha(reserva.updatedAt)?.getTime() ?? 0,
      ...eventos.map((e) => momentoDe(e))
    );
    const paraOrdenar = cancelledAt ?? new Date(ultimoConocido + 1);

    // De dónde se canceló no se guardaba. Lo único deducible es si había
    // sesión: `cancelledBy: 'customer'` significa que NO la había, y el panel
    // exige sesión, así que entonces no pudo ser el panel. Con sesión no se
    // puede distinguir entre panel y formulario, y se dice.
    const sinSesion = reserva.cancelledBy === 'customer';
    eventos.push({
      ...base,
      type: 'cancelled',
      at: sinHora(paraOrdenar),
      clientAt: paraOrdenar,
      ...(cancelledAt
        ? {}
        : {
            notaFecha: {
              corta: 'No se registró cuándo',
              detalle:
                'Se canceló antes de que existiera el registro de eventos, y entonces no ' +
                'se guardaba la hora. Aparece al final porque después de cancelar no se ' +
                'puede hacer nada más.',
            },
          }),
      ...(sinSesion
        ? {}
        : { origenNota: 'Se hizo con sesión iniciada, pero no se registró desde dónde' }),
      actor,
      origin: sinSesion
        ? {
            channel: 'form',
            from: null,
            parentDomain: null,
            withCode: false,
            device: 'no registrado',
          }
        : origen(),
      changes: [],
      summary: redactarResumen('cancelled', actor, [], locationName),
    });
  }

  return eventos;
};

/** Milisegundos de un evento, prefiriendo la hora del servidor. */
export const momentoDe = (evento: EventoReserva): number => {
  const v = (evento.at ?? evento.clientAt) as unknown as { toDate?: () => Date };
  if (v && typeof v.toDate === 'function') return v.toDate().getTime();
  const d = new Date(v as unknown as string);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
};

/* ─────────────────────  cómo se cuenta el origen  ───────────────────── */

/**
 * Dónde ocurrió la acción, dicho con precisión y **sin afirmar lo que no se
 * sabe**.
 *
 * El orden importa. Un cliente puede llegar a la misma pantalla de dos maneras
 * distintas: pulsando el enlace de su correo, o entrando a la web del cliente
 * y tecleando allí su código. Si se detectó el dominio que embebe el
 * formulario, esa es la respuesta más concreta y gana. Solo cuando no hay
 * dominio tiene sentido decir que vino del correo.
 *
 * En los eventos reconstruidos no se inventa un origen: de aquellas reservas
 * solo se guardó si venían del panel o del formulario, nunca desde qué web.
 */
export const describirDondeOcurrio = (
  origen?: OrigenEvento | null,
  reconstruido?: boolean,
  nota?: string
): string => {
  // La nota manda: se escribe donde se sabe exactamente qué se puede afirmar.
  if (nota) return nota;
  if (!origen) return 'Origen no registrado';
  if (origen.channel === 'dashboard') return 'Desde el panel de reservas';
  if (origen.parentDomain) return `Desde el formulario en ${origen.parentDomain}`;
  if (origen.withCode) return 'Desde el enlace del correo';
  if (reconstruido) return 'Desde el formulario';
  return 'Desde el formulario, sin identificar la web';
};

/** Versión corta para las listas, donde el espacio manda. */
export const dondeOcurrioCorto = (origen?: OrigenEvento | null): string => {
  if (!origen) return 'sin registrar';
  if (origen.channel === 'dashboard') return 'Panel';
  if (origen.parentDomain) return origen.parentDomain;
  if (origen.withCode) return 'Enlace del correo';
  return 'Formulario';
};
